'use client';

import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { pictureBookTemplates, templateThumbnailFor, templateTitleFor } from '@/lib/picture-book/templates';
import { pictureBookTemplateFitsCast } from '@/lib/picture-book/casting';
import { centralNeedsLine, unfilledCentralRoles } from '@/lib/picture-book/central-roles';
import type { UnifiedStoryTemplate } from '@/lib/unified-story-templates';
import type { MangaCharacterDraft } from '@/components/manga/types';

const CATEGORY_LABELS: Record<UnifiedStoryTemplate['category'], string> = {
  adventure: 'Adventure',
  fantasy: 'Fantasy',
  animals: 'Animals',
  mystery: 'Mystery',
  science: 'Science',
  sports: 'Sports',
  occasions: 'Special occasions',
  educational: 'Learning',
  learning: 'Learning',
};

interface PictureBookTemplatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  people: MangaCharacterDraft[];
  onPick: (template: UnifiedStoryTemplate) => void;
}

/**
 * The templates this cast can make, in catalogue order. A template that does not fit the cast (new-sibling with no
 * child to become the big sibling, e.g. a newborn alone) is not offered. A template whose central role nobody in the
 * cast can play (grandparents-day without a grandparent) IS kept: the picker shows it disabled with its needs line
 * (pickerNeedsLine; owner default, family-from-photo Task B3). Pure.
 */
export function pickerTemplates(people: PickerPerson[]): UnifiedStoryTemplate[] {
  const cast = pickerCast(people);
  return pictureBookTemplates().filter((t) => pictureBookTemplateFitsCast(t.id, cast));
}

type PickerPerson = Pick<MangaCharacterDraft, 'name' | 'age' | 'isMainCharacter' | 'relationship'>;

/** 'Needs a grandparent in your photo' when this cast cannot play the template's central role, else null. Pure. */
export function pickerNeedsLine(templateId: string, people: PickerPerson[]): string | null {
  return unfilledCentralRoles(templateId, pickerCast(people)).length ? centralNeedsLine(templateId) : null;
}

/** The cast the picker filters and titles with (the relationship marks a baby with no age). Pure. */
export function pickerCast(people: PickerPerson[]) {
  return people.map((p) => ({ name: p.name.trim(), age: p.age, isMainCharacter: p.isMainCharacter, relationship: p.relationship }));
}

/** "Pick a ready-made story": the picture-book templates, grouped by category, titles personalized with the cast. */
export function PictureBookTemplatePicker({ open, onOpenChange, people, onPick }: PictureBookTemplatePickerProps) {
  const groups = useMemo(() => {
    const byLabel = new Map<string, UnifiedStoryTemplate[]>();
    for (const t of pickerTemplates(people)) {
      const label = CATEGORY_LABELS[t.category] ?? 'More stories';
      byLabel.set(label, [...(byLabel.get(label) ?? []), t]);
    }
    return [...byLabel.entries()];
  }, [people]);
  const cast = pickerCast(people);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pick a ready-made story</DialogTitle>
          <DialogDescription>We fill in the title and the idea. You can still change both.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {groups.map(([label, templates]) => (
            <section key={label} aria-label={label}>
              <h3 className="mb-2 text-sm font-semibold text-gray-700">{label}</h3>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {templates.map((t) => {
                  const thumb = templateThumbnailFor(t);
                  const needs = pickerNeedsLine(t.id, people);
                  // A disabled card dims its thumbnail and text; the needs line stays full contrast
                  const dim = needs ? ' opacity-60' : '';
                  return (
                    <li key={t.id} data-template={t.id}>
                      <button
                        type="button"
                        onClick={() => onPick(t)}
                        disabled={!!needs}
                        className={`flex w-full items-start gap-3 rounded-lg border-2 border-gray-200 p-3 text-left transition-colors focus-visible:border-purple-500 focus-visible:outline-none ${
                          needs ? 'cursor-not-allowed' : 'hover:border-purple-300'
                        }`}
                      >
                        {thumb && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={thumb} alt="" className={`h-16 w-16 flex-none rounded-md object-cover${dim}`} loading="lazy" />
                        )}
                        <span className="min-w-0 space-y-1">
                          <span className={`block font-semibold text-gray-800${dim}`}>{templateTitleFor(t, cast)}</span>
                          <span className={`block text-sm text-gray-600${dim}`}>{t.description}</span>
                          <span className={`inline-block rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800${dim}`}>
                            ages {t.ageRange.min}–{t.ageRange.max}
                          </span>
                          {needs && <span className="block text-sm font-medium text-amber-800">{needs}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
